/**
 * AdminProductEditModal
 *
 * Used for both creating a new product and editing an existing one.
 * Sections: core fields, MSRP pricing, images, meta key/value notes,
 * broadcast picker (create-only), delete (edit-only, super only).
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Save, Loader2, Plus, Trash2, Star, StarOff, Image as ImageIcon, AlertTriangle, Send, CheckCircle2,
  ChevronRight, ChevronDown, Barcode,
} from 'lucide-react';
import {
  AdminColor,
  AdminProduct,
  AdminProductImage,
  AdminProductMeta,
  AdminRetailerSummary,
  AdminProductFieldDef,
  AdminRetailerCoverage,
  ProductCreatePayload,
  ProductPatchPayload,
  createAdminProduct,
  fetchAdminProduct,
  patchAdminProduct,
  pushProductToRetailers,
} from '../../services/adminProductsApi';

interface Props {
  productId: number | null;
  isCreating: boolean;
  retailers: AdminRetailerSummary[];
  colors: AdminColor[];
  categories: string[];
  canEdit: boolean;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: (id: number, sku: string) => void;
}

interface ImageDraft extends Omit<AdminProductImage, 'id'> {
  id?: number;
  tempKey: string;
}

interface MetaDraft extends Omit<AdminProductMeta, 'id'> {
  id?: number;
  tempKey: string;
}

const newTempKey = (): string => Math.random().toString(36).slice(2);

/**
 * A titled, collapsible card section (collapsed by default). The header toggle
 * is a div (not a button) so it keeps working even though the modal body is
 * inside a disabled <fieldset> — a fieldset only disables real form controls,
 * which also lets read-only viewers expand sections. `headerRight` renders an
 * action (e.g. "Add Image") beside the title without nesting it in the toggle.
 */
const CollapsibleSection: React.FC<{
  title: React.ReactNode;
  icon?: React.ReactNode;
  subtitle?: React.ReactNode;
  defaultOpen?: boolean;
  headerRight?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, icon, subtitle, defaultOpen = false, headerRight, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border rounded-3 mb-3">
      <div className="d-flex align-items-center justify-content-between">
        <div
          role="button"
          tabIndex={0}
          className="d-flex align-items-center gap-2 fw-bold py-2 px-3 flex-grow-1"
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => setOpen(o => !o)}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(o => !o); } }}
        >
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          {icon}
          {title}
          {subtitle && <span className="text-muted small fw-normal ms-1">{subtitle}</span>}
        </div>
        {headerRight && <div className="pe-3">{headerRight}</div>}
      </div>
      {open && <div className="px-3 pb-3 pt-1">{children}</div>}
    </div>
  );
};

const blankProduct = (): AdminProduct => ({
  id: 0,
  productId: '',
  sku: '',
  name: '',
  acronym: '',
  category: '',
  color: '',
  lengthFeet: 0,
  sqft: 0,
  description: null,
  gs1Prefix: null,
  gtin: null,
  gtin8: null,
  upc: null,
  ean: null,
  isActive: true,
  msrpUsd: 0,
  msrpCad: 0,
  msrpActive: true,
  createdAt: '',
  updatedAt: '',
});

export const AdminProductEditModal: React.FC<Props> = ({
  productId, isCreating, retailers, colors, categories, canEdit, onClose, onSaved, onDeleted,
}) => {
  const [product, setProduct] = useState<AdminProduct | null>(isCreating ? blankProduct() : null);
  const [images, setImages] = useState<ImageDraft[]>([]);
  const [meta, setMeta] = useState<MetaDraft[]>([]);
  const [loading, setLoading] = useState(!isCreating);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [broadcastMode, setBroadcastMode] = useState<'none' | 'all' | 'subset'>('none');
  const [broadcastIds, setBroadcastIds] = useState<number[]>([]);

  // Push-to-retailers (edit mode): which external retailers already carry the
  // product, plus the picker + in-flight state for a push.
  const [coverage, setCoverage] = useState<AdminRetailerCoverage[]>([]);
  const [pushMode, setPushMode] = useState<'all' | 'subset'>('subset');
  const [pushIds, setPushIds] = useState<number[]>([]);
  const [pushing, setPushing] = useState(false);
  const [pushError, setPushError] = useState<string | null>(null);
  const [pushResult, setPushResult] = useState<string | null>(null);

  // Trusscore-default custom fields: definitions + edited values keyed by defId.
  const [fieldDefs, setFieldDefs] = useState<AdminProductFieldDef[]>([]);
  const [fieldValues, setFieldValues] = useState<Record<number, string>>({});

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteSkuInput, setDeleteSkuInput] = useState('');

  const [visible, setVisible] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    if (isCreating || !productId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const detail = await fetchAdminProduct(productId);
        if (cancelled) return;
        setProduct(detail.product);
        setImages(detail.images.map(i => ({ ...i, tempKey: newTempKey() })));
        setMeta(detail.meta.map(m => ({ ...m, tempKey: newTempKey() })));
        setFieldDefs(detail.fieldDefs ?? []);
        setCoverage(detail.retailerCoverage ?? []);
        const valueMap: Record<number, string> = {};
        (detail.fieldValues ?? []).forEach(v => { valueMap[v.fieldDefId] = v.value ?? ''; });
        setFieldValues(valueMap);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [productId, isCreating]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setVisible(false);
        setTimeout(onClose, 250);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 250);
  };

  const externalRetailers = useMemo(() => retailers.filter(r => r.id !== 1), [retailers]);

  // Retailer ids that already carry this product — these are skipped on push.
  const coverageSet = useMemo(() => new Set(coverage.map(c => c.retailerId)), [coverage]);

  const handlePush = async () => {
    if (!canEdit || isCreating || !productId) return;
    const targetIds = pushMode === 'subset'
      ? pushIds.filter(id => !coverageSet.has(id))
      : undefined;
    if (pushMode === 'subset' && (!targetIds || targetIds.length === 0)) {
      setPushError('Select at least one retailer that doesn’t already carry this product.');
      return;
    }
    setPushing(true);
    setPushError(null);
    setPushResult(null);
    try {
      const result = await pushProductToRetailers(productId, { mode: pushMode, retailerIds: targetIds });
      setCoverage(result.coverage);
      setPushIds([]);
      setPushResult(
        `Pushed to ${result.pushedCount} retailer${result.pushedCount === 1 ? '' : 's'}` +
        (result.skippedCount > 0 ? `, ${result.skippedCount} already had it.` : '.'),
      );
    } catch (err) {
      setPushError(err instanceof Error ? err.message : 'Push failed');
    } finally {
      setPushing(false);
    }
  };

  const updateField = <K extends keyof AdminProduct>(key: K, value: AdminProduct[K]) => {
    setProduct(prev => prev ? { ...prev, [key]: value } : prev);
  };

  const addImage = () => {
    setImages(prev => [
      ...prev,
      { tempKey: newTempKey(), url: '', altText: null, sortOrder: prev.length, isPrimary: prev.length === 0 },
    ]);
  };

  const updateImage = (key: string, patch: Partial<ImageDraft>) => {
    setImages(prev => prev.map(i => i.tempKey === key ? { ...i, ...patch } : i));
  };

  const removeImage = (key: string) => {
    setImages(prev => prev.filter(i => i.tempKey !== key));
  };

  const setPrimary = (key: string) => {
    setImages(prev => prev.map(i => ({ ...i, isPrimary: i.tempKey === key })));
  };

  const addMeta = () => {
    setMeta(prev => [...prev, { tempKey: newTempKey(), key: '', value: '', sortOrder: prev.length }]);
  };

  const updateMeta = (key: string, patch: Partial<MetaDraft>) => {
    setMeta(prev => prev.map(m => m.tempKey === key ? { ...m, ...patch } : m));
  };

  const removeMeta = (key: string) => {
    setMeta(prev => prev.filter(m => m.tempKey !== key));
  };

  const validate = (): string | null => {
    if (!product) return 'No product loaded';
    if (!product.productId.trim()) return 'Product ID is required';
    if (!product.sku.trim()) return 'SKU is required';
    if (!product.name.trim()) return 'Name is required';
    if (!product.acronym.trim()) return 'Acronym is required';
    if (!product.category.trim()) return 'Category is required';
    if (!product.color.trim()) return 'Color is required';
    if (product.lengthFeet < 0 || product.sqft < 0) return 'Length and sqft must be non-negative';
    if ((product.msrpUsd ?? 0) < 0 || (product.msrpCad ?? 0) < 0) return 'MSRP pricing must be non-negative';
    return null;
  };

  const handleSave = async () => {
    if (!canEdit || !product) return;
    const err = validate();
    if (err) { setError(err); return; }

    setSaving(true);
    setError(null);
    try {
      if (isCreating) {
        const payload: ProductCreatePayload = {
          product: {
            productId: product.productId.trim(),
            sku: product.sku.trim(),
            name: product.name.trim(),
            acronym: product.acronym.trim(),
            category: product.category,
            color: product.color,
            lengthFeet: product.lengthFeet,
            sqft: product.sqft,
            description: product.description,
            gs1Prefix: product.gs1Prefix,
            gtin: product.gtin,
            gtin8: product.gtin8,
            upc: product.upc,
            ean: product.ean,
            isActive: product.isActive,
          },
          msrp: { priceUsd: product.msrpUsd ?? 0, priceCad: product.msrpCad ?? 0 },
          broadcast: {
            mode: broadcastMode,
            retailerIds: broadcastMode === 'subset' ? broadcastIds : undefined,
          },
        };
        const created = await createAdminProduct(payload);
        // Save images/meta/field values in a follow-up patch (create endpoint
        // focuses on the product + broadcast atomicity).
        const fieldValuePatch = fieldDefs
          .filter(d => (fieldValues[d.id] ?? '').trim() !== '')
          .map(d => ({ fieldDefId: d.id, value: fieldValues[d.id] }));
        if (images.length > 0 || meta.length > 0 || fieldValuePatch.length > 0) {
          await patchAdminProduct(created.product.id, {
            images: images.filter(i => i.url.trim()).map(i => ({
              url: i.url.trim(),
              altText: i.altText,
              sortOrder: i.sortOrder,
              isPrimary: i.isPrimary,
            })),
            meta: meta.filter(m => m.key.trim()).map(m => ({
              key: m.key.trim(),
              value: m.value,
              sortOrder: m.sortOrder,
            })),
            fieldValues: fieldValuePatch,
          });
        }
      } else if (productId) {
        const payload: ProductPatchPayload = {
          product: {
            productId: product.productId.trim(),
            sku: product.sku.trim(),
            name: product.name.trim(),
            acronym: product.acronym.trim(),
            category: product.category,
            color: product.color,
            lengthFeet: product.lengthFeet,
            sqft: product.sqft,
            description: product.description,
            gs1Prefix: product.gs1Prefix,
            gtin: product.gtin,
            gtin8: product.gtin8,
            upc: product.upc,
            ean: product.ean,
            isActive: product.isActive,
          },
          msrp: { priceUsd: product.msrpUsd ?? 0, priceCad: product.msrpCad ?? 0 },
          images: images.filter(i => i.url.trim()).map(i => ({
            url: i.url.trim(),
            altText: i.altText,
            sortOrder: i.sortOrder,
            isPrimary: i.isPrimary,
          })),
          meta: meta.filter(m => m.key.trim()).map(m => ({
            key: m.key.trim(),
            value: m.value,
            sortOrder: m.sortOrder,
          })),
          // Send every def's value (empty string deletes server-side).
          fieldValues: fieldDefs.map(d => ({ fieldDefId: d.id, value: fieldValues[d.id] ?? null })),
        };
        await patchAdminProduct(productId, payload);
      }
      onSaved();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const skuMatches = product && deleteSkuInput.trim() === product.sku;

  if (!product && !loading) {
    return null;
  }

  return (
    <div
      className={`modal d-block fade ${visible ? 'show' : ''}`}
      tabIndex={-1}
      style={{ transition: 'opacity 0.25s ease', backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title fw-bold">
              {isCreating ? 'Add Product' : `Edit Product${product ? ` — ${product.sku}` : ''}`}
              {!canEdit && <span className="badge text-bg-secondary ms-2">Read-only</span>}
            </h5>
            <button type="button" className="btn-close" onClick={handleClose} aria-label="Close" />
          </div>

          <div className="modal-body">
            {loading ? (
              <div className="text-center py-5 text-muted">
                <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} /> Loading…
              </div>
            ) : !product ? null : (
              <>
                {error && (
                  <div className="alert alert-warning py-2 px-3 small d-flex align-items-center gap-2">
                    <AlertTriangle size={14} /> {error}
                  </div>
                )}

                {/* Core fields */}
                <fieldset disabled={!canEdit || saving}>
                  <h6 className="fw-bold mb-2">Identifiers</h6>
                  <div className="row g-2 mb-3">
                    <div className="col-sm-6">
                      <label className="form-label small fw-semibold mb-1">Product ID (slug)</label>
                      <input
                        type="text"
                        className="form-control form-control-sm font-monospace"
                        value={product.productId}
                        maxLength={50}
                        onChange={e => updateField('productId', e.target.value)}
                      />
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label small fw-semibold mb-1">SKU</label>
                      <input
                        type="text"
                        className="form-control form-control-sm font-monospace"
                        value={product.sku}
                        maxLength={50}
                        onChange={e => updateField('sku', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label small fw-semibold mb-1">Name</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={product.name}
                      maxLength={255}
                      onChange={e => updateField('name', e.target.value)}
                    />
                  </div>

                  <div className="row g-2 mb-3">
                    <div className="col-sm-3">
                      <label className="form-label small fw-semibold mb-1">Acronym</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={product.acronym}
                        maxLength={20}
                        onChange={e => updateField('acronym', e.target.value)}
                      />
                    </div>
                    <div className="col-sm-5">
                      <label className="form-label small fw-semibold mb-1">Category</label>
                      <select
                        className="form-select form-select-sm"
                        value={product.category}
                        onChange={e => updateField('category', e.target.value)}
                      >
                        <option value="">Select…</option>
                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="col-sm-4">
                      <label className="form-label small fw-semibold mb-1">Color</label>
                      <select
                        className="form-select form-select-sm"
                        value={product.color}
                        onChange={e => updateField('color', e.target.value)}
                      >
                        <option value="">Select…</option>
                        {colors.filter(c => c.isActive || c.slug === product.color).map(c => (
                          <option key={c.id} value={c.slug}>{c.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="row g-2 mb-3">
                    <div className="col-sm-3">
                      <label className="form-label small fw-semibold mb-1">Length (ft)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="form-control form-control-sm"
                        value={product.lengthFeet}
                        onChange={e => updateField('lengthFeet', Number(e.target.value))}
                      />
                    </div>
                    <div className="col-sm-3">
                      <label className="form-label small fw-semibold mb-1">Sqft</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="form-control form-control-sm"
                        value={product.sqft}
                        onChange={e => updateField('sqft', Number(e.target.value))}
                      />
                    </div>
                    <div className="col-sm-6 d-flex align-items-end">
                      <div className="form-check form-switch">
                        <input
                          id="product-active"
                          type="checkbox"
                          className="form-check-input"
                          role="switch"
                          checked={product.isActive}
                          onChange={e => updateField('isActive', e.target.checked)}
                        />
                        <label className="form-check-label small fw-semibold" htmlFor="product-active">
                          Active
                          <span className="text-muted ms-2">
                            (deactivating cascades to all retailers)
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label small fw-semibold mb-1">Description</label>
                    <textarea
                      className="form-control form-control-sm"
                      rows={2}
                      value={product.description ?? ''}
                      onChange={e => updateField('description', e.target.value || null)}
                    />
                  </div>

                  {/* GS1 product codes — collapsible */}
                  <CollapsibleSection
                    icon={<Barcode size={15} />}
                    title="Product Codes (GS1)"
                    subtitle="UPC · GTIN · EAN"
                  >
                    <p className="text-muted small mb-2">
                      One set of codes per product (no packaging levels). Used as
                      barcode &amp; token sources in the Column Layout / Custom Tables
                      designers. Leave a field blank to clear it.
                    </p>
                    <div className="row g-2">
                      <div className="col-sm-4">
                        <label className="form-label small fw-semibold mb-1">UPC (GTIN-12)</label>
                        <input
                          type="text" inputMode="numeric"
                          className="form-control form-control-sm font-monospace"
                          value={product.upc ?? ''}
                          maxLength={20}
                          onChange={e => updateField('upc', e.target.value || null)}
                        />
                      </div>
                      <div className="col-sm-4">
                        <label className="form-label small fw-semibold mb-1">GTIN (14-digit)</label>
                        <input
                          type="text" inputMode="numeric"
                          className="form-control form-control-sm font-monospace"
                          value={product.gtin ?? ''}
                          maxLength={20}
                          onChange={e => updateField('gtin', e.target.value || null)}
                        />
                      </div>
                      <div className="col-sm-4">
                        <label className="form-label small fw-semibold mb-1">EAN (GTIN-13)</label>
                        <input
                          type="text" inputMode="numeric"
                          className="form-control form-control-sm font-monospace"
                          value={product.ean ?? ''}
                          maxLength={20}
                          onChange={e => updateField('ean', e.target.value || null)}
                        />
                      </div>
                      <div className="col-sm-4">
                        <label className="form-label small fw-semibold mb-1">GTIN-8</label>
                        <input
                          type="text" inputMode="numeric"
                          className="form-control form-control-sm font-monospace"
                          value={product.gtin8 ?? ''}
                          maxLength={20}
                          onChange={e => updateField('gtin8', e.target.value || null)}
                        />
                      </div>
                      <div className="col-sm-4">
                        <label className="form-label small fw-semibold mb-1">GS1 Company Prefix</label>
                        <input
                          type="text" inputMode="numeric"
                          className="form-control form-control-sm font-monospace"
                          value={product.gs1Prefix ?? ''}
                          maxLength={20}
                          onChange={e => updateField('gs1Prefix', e.target.value || null)}
                        />
                      </div>
                    </div>
                  </CollapsibleSection>

                  {/* MSRP pricing — collapsible */}
                  <CollapsibleSection title="MSRP Pricing (Retailer #1)">
                    <div className="row g-2">
                      <div className="col-sm-6">
                        <label className="form-label small fw-semibold mb-1">Price USD</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="form-control form-control-sm"
                          value={product.msrpUsd ?? 0}
                          onChange={e => updateField('msrpUsd', Number(e.target.value))}
                        />
                      </div>
                      <div className="col-sm-6">
                        <label className="form-label small fw-semibold mb-1">Price CAD</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="form-control form-control-sm"
                          value={product.msrpCad ?? 0}
                          onChange={e => updateField('msrpCad', Number(e.target.value))}
                        />
                      </div>
                    </div>
                  </CollapsibleSection>

                  {/* Images — collapsible */}
                  <CollapsibleSection
                    icon={<ImageIcon size={14} />}
                    title="Product Images"
                    headerRight={canEdit && (
                      <button type="button" className="btn btn-outline-primary btn-sm" onClick={addImage}>
                        <Plus size={12} /> Add Image
                      </button>
                    )}
                  >
                  {images.length === 0 ? (
                    <p className="text-muted small mb-0">No images yet.</p>
                  ) : (
                    <div className="vstack gap-2">
                      {images.map(img => (
                        <div key={img.tempKey} className="border rounded-3 p-2 d-flex gap-2 align-items-start">
                          {img.url && (
                            <img
                              src={img.url}
                              alt={img.altText ?? ''}
                              style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4, background: '#f3f4f6' }}
                              onError={e => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }}
                            />
                          )}
                          <div className="flex-grow-1 d-flex flex-column gap-1">
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Image URL"
                              value={img.url}
                              onChange={e => updateImage(img.tempKey, { url: e.target.value })}
                            />
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Alt text (optional)"
                              value={img.altText ?? ''}
                              onChange={e => updateImage(img.tempKey, { altText: e.target.value || null })}
                            />
                          </div>
                          <div className="btn-group-vertical btn-group-sm">
                            <button
                              type="button"
                              className="btn btn-outline-warning"
                              title={img.isPrimary ? 'Primary' : 'Mark primary'}
                              onClick={() => setPrimary(img.tempKey)}
                              disabled={!canEdit}
                            >
                              {img.isPrimary ? <Star size={12} /> : <StarOff size={12} />}
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-danger"
                              onClick={() => removeImage(img.tempKey)}
                              disabled={!canEdit}
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  </CollapsibleSection>

                  {/* Notes & Metadata — collapsible */}
                  <CollapsibleSection
                    title="Notes & Metadata"
                    headerRight={canEdit && (
                      <button type="button" className="btn btn-outline-primary btn-sm" onClick={addMeta}>
                        <Plus size={12} /> Add Note
                      </button>
                    )}
                  >
                  {meta.length === 0 ? (
                    <p className="text-muted small mb-0">No notes yet. Use this for things like "Shipping Restriction" or "Care Instructions".</p>
                  ) : (
                    <div className="vstack gap-2">
                      {meta.map(m => (
                        <div key={m.tempKey} className="d-flex gap-2 align-items-start">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Key (e.g. Shipping Restriction)"
                            style={{ maxWidth: 220 }}
                            value={m.key}
                            maxLength={100}
                            onChange={e => updateMeta(m.tempKey, { key: e.target.value })}
                          />
                          <textarea
                            className="form-control form-control-sm flex-grow-1"
                            placeholder="Value"
                            rows={1}
                            value={m.value ?? ''}
                            onChange={e => updateMeta(m.tempKey, { value: e.target.value })}
                          />
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => removeMeta(m.tempKey)}
                            disabled={!canEdit}
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  </CollapsibleSection>

                  {/* Trusscore-default custom fields */}
                  {fieldDefs.length > 0 && (
                    <div className="mt-4">
                      <h6 className="fw-bold mb-1">Custom Fields (Trusscore default)</h6>
                      <p className="text-muted small mb-2">
                        Global field values for this product. Manage the field definitions in
                        the <strong>Product Fields</strong> admin page.
                      </p>
                      <div className="vstack gap-2 mb-3">
                        {fieldDefs.map(d => (
                          <div key={d.id} className="d-flex gap-2 align-items-center">
                            <label className="form-label small fw-semibold mb-0" style={{ minWidth: 180 }}>
                              {d.label}
                              {!d.isActive && <span className="badge text-bg-secondary ms-1" style={{ fontSize: '0.6rem' }}>off</span>}
                            </label>
                            <input
                              type={d.dataType === 'numeric' ? 'number' : 'text'}
                              className="form-control form-control-sm"
                              value={fieldValues[d.id] ?? ''}
                              disabled={!canEdit}
                              onChange={e => setFieldValues(prev => ({ ...prev, [d.id]: e.target.value }))}
                              placeholder={d.dataType === 'url' ? 'value or URL' : ''}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Broadcast — create only */}
                  {isCreating && externalRetailers.length > 0 && (
                    <>
                      <h6 className="fw-bold mb-2 mt-4">Inherit to Retailers</h6>
                      <p className="text-muted small mb-2">
                        New product is always added to retailer&nbsp;#1 at the MSRP above.
                        Pick where else it should appear — selected retailers get the
                        MSRP price but with <strong>active=off</strong> and a review flag
                        for their next admin login.
                      </p>
                      <div className="d-flex gap-3 mb-2">
                        <div className="form-check">
                          <input
                            type="radio"
                            className="form-check-input"
                            id="bc-none"
                            checked={broadcastMode === 'none'}
                            onChange={() => setBroadcastMode('none')}
                          />
                          <label className="form-check-label small" htmlFor="bc-none">No additional retailers</label>
                        </div>
                        <div className="form-check">
                          <input
                            type="radio"
                            className="form-check-input"
                            id="bc-all"
                            checked={broadcastMode === 'all'}
                            onChange={() => setBroadcastMode('all')}
                          />
                          <label className="form-check-label small" htmlFor="bc-all">All retailers</label>
                        </div>
                        <div className="form-check">
                          <input
                            type="radio"
                            className="form-check-input"
                            id="bc-subset"
                            checked={broadcastMode === 'subset'}
                            onChange={() => setBroadcastMode('subset')}
                          />
                          <label className="form-check-label small" htmlFor="bc-subset">Pick subset</label>
                        </div>
                      </div>
                      {broadcastMode === 'subset' && (
                        <div className="border rounded-3 p-2" style={{ maxHeight: 160, overflowY: 'auto' }}>
                          {externalRetailers.map(r => {
                            const checked = broadcastIds.includes(r.id);
                            return (
                              <div key={r.id} className="form-check small">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id={`bc-r-${r.id}`}
                                  checked={checked}
                                  onChange={e => {
                                    if (e.target.checked) setBroadcastIds(prev => [...prev, r.id]);
                                    else setBroadcastIds(prev => prev.filter(id => id !== r.id));
                                  }}
                                />
                                <label className="form-check-label" htmlFor={`bc-r-${r.id}`}>
                                  {r.name} {!r.isActive && <span className="text-muted">(inactive)</span>}
                                </label>
                              </div>
                            );
                          })}
                          {externalRetailers.length === 0 && <p className="text-muted small mb-0">No external retailers yet.</p>}
                        </div>
                      )}
                    </>
                  )}

                  {/* Push to Retailers — edit only, super only — collapsible */}
                  {!isCreating && canEdit && externalRetailers.length > 0 && (
                    <CollapsibleSection icon={<Send size={14} />} title="Push to Retailers">
                      <p className="text-muted small mb-2">
                        Add this product to other retailers. Targets that already carry it
                        are skipped — new entries land at the current saved MSRP price with
                        <strong> active=off</strong> and a review flag for the retailer’s next
                        admin login. Pushing uses the saved MSRP; save pricing edits first.
                      </p>

                      <div className="d-flex gap-3 mb-2">
                        <div className="form-check">
                          <input
                            type="radio"
                            className="form-check-input"
                            id="push-subset"
                            checked={pushMode === 'subset'}
                            onChange={() => { setPushMode('subset'); setPushResult(null); setPushError(null); }}
                          />
                          <label className="form-check-label small" htmlFor="push-subset">Pick retailers</label>
                        </div>
                        <div className="form-check">
                          <input
                            type="radio"
                            className="form-check-input"
                            id="push-all"
                            checked={pushMode === 'all'}
                            onChange={() => { setPushMode('all'); setPushResult(null); setPushError(null); }}
                          />
                          <label className="form-check-label small" htmlFor="push-all">All retailers (skips those that have it)</label>
                        </div>
                      </div>

                      {pushMode === 'subset' && (
                        <div className="border rounded-3 p-2 mb-2" style={{ maxHeight: 180, overflowY: 'auto' }}>
                          {externalRetailers.map(r => {
                            const has = coverageSet.has(r.id);
                            const checked = pushIds.includes(r.id);
                            return (
                              <div key={r.id} className="form-check small d-flex align-items-center gap-2">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id={`push-r-${r.id}`}
                                  checked={has || checked}
                                  disabled={has}
                                  onChange={e => {
                                    if (e.target.checked) setPushIds(prev => [...prev, r.id]);
                                    else setPushIds(prev => prev.filter(id => id !== r.id));
                                  }}
                                />
                                <label className="form-check-label" htmlFor={`push-r-${r.id}`}>
                                  {r.name} {!r.isActive && <span className="text-muted">(inactive)</span>}
                                </label>
                                {has && (
                                  <span className="badge text-bg-success-subtle text-success border border-success-subtle" style={{ fontSize: '0.65rem' }}>
                                    <CheckCircle2 size={10} className="me-1" />already has it
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {pushError && (
                        <div className="alert alert-warning py-2 px-3 small d-flex align-items-center gap-2 mb-2">
                          <AlertTriangle size={14} /> {pushError}
                        </div>
                      )}
                      {pushResult && (
                        <div className="alert alert-success py-2 px-3 small d-flex align-items-center gap-2 mb-2">
                          <CheckCircle2 size={14} /> {pushResult}
                        </div>
                      )}

                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
                        onClick={handlePush}
                        disabled={pushing || saving}
                      >
                        {pushing ? (
                          <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Pushing…</>
                        ) : (
                          <><Send size={14} /> Push Now</>
                        )}
                      </button>
                    </CollapsibleSection>
                  )}

                  {/* Delete — edit only, super only */}
                  {!isCreating && canEdit && (
                    <div className="border-top pt-3 mt-4">
                      {!confirmDelete ? (
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm d-inline-flex align-items-center gap-1"
                          onClick={() => setConfirmDelete(true)}
                        >
                          <Trash2 size={14} /> Delete Product
                        </button>
                      ) : (
                        <div className="border border-danger rounded-3 p-3 bg-danger-subtle">
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <AlertTriangle size={18} className="text-danger" />
                            <strong className="text-danger">Delete this product permanently?</strong>
                          </div>
                          <p className="small mb-2">
                            This wipes the product, its MSRP row, <strong>every retailer's
                            child row</strong> for it, all images, and all metadata. This
                            cannot be undone.
                          </p>
                          <p className="small mb-2">
                            To confirm, type the SKU <code className="fw-bold">{product.sku}</code> below:
                          </p>
                          <input
                            type="text"
                            className="form-control form-control-sm font-monospace mb-2"
                            value={deleteSkuInput}
                            onChange={e => setDeleteSkuInput(e.target.value)}
                            placeholder="SKU"
                          />
                          <div className="d-flex gap-2">
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => { setConfirmDelete(false); setDeleteSkuInput(''); }}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="btn btn-danger btn-sm d-inline-flex align-items-center gap-1"
                              disabled={!skuMatches || saving}
                              onClick={() => onDeleted(product.id, product.sku)}
                            >
                              <Trash2 size={14} /> Confirm Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </fieldset>
              </>
            )}
          </div>

          <div className="modal-footer bg-light">
            <button type="button" className="btn btn-outline-secondary" onClick={handleClose} disabled={saving}>
              {canEdit ? 'Cancel' : 'Close'}
            </button>
            {canEdit && (
              <button
                type="button"
                className="btn btn-primary d-inline-flex align-items-center gap-1"
                onClick={handleSave}
                disabled={saving || loading}
              >
                {saving ? (
                  <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Saving…</>
                ) : (
                  <><Save size={16} /> {isCreating ? 'Create Product' : 'Save Changes'}</>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminProductEditModal;
